import { useState } from "react";
import { Plus, Search, Users, MessageCircle } from "lucide-react";
import { motion } from "framer-motion";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { Chat } from "@/hooks/useMessaging";
import { NewChatDialog } from "./NewChatDialog";

interface ChatListProps {
  chats: Chat[];
  currentUserId: string | null;
  loading: boolean;
  onSelectChat: (chatId: string) => void;
  getChatDisplayName: (chat: Chat) => string;
  getChatAvatar: (chat: Chat) => string | null;
  onAddContact: (email: string) => Promise<string | null>;
  contacts: any[];
  onCreateChat: (participantIds: string[], isGroup: boolean, groupName?: string) => Promise<string | null>;
}

export const ChatList = ({
  chats, currentUserId, loading, onSelectChat,
  getChatDisplayName, getChatAvatar, onAddContact, contacts, onCreateChat
}: ChatListProps) => {
  const [search, setSearch] = useState("");
  const [showNew, setShowNew] = useState(false);

  const filtered = chats.filter((c) => getChatDisplayName(c).toLowerCase().includes(search.toLowerCase()));

  const getLastMessagePreview = (chat: Chat) => {
    if (!chat.last_message) return "Nessun messaggio";
    const m = chat.last_message;
    if (m.message_type === "image") return "📷 Foto";
    if (m.message_type === "audio") return "🎤 Nota vocale";
    return m.content || "";
  };

  const getLastMessageTime = (chat: Chat) => {
    if (!chat.last_message) return "";
    const d = new Date(chat.last_message.created_at);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
  };

  const getUnreadCount = (chat: Chat) => {
    // Simplified - would need separate query for accurate count
    if (chat.last_message && !chat.last_message.is_read && chat.last_message.sender_id !== currentUserId) return 1;
    return 0;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 pt-4 pb-2 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-2xl font-bold text-foreground">Messaggi</h2>
          <button
            onClick={() => setShowNew(true)}
            className="p-2 rounded-full bg-primary/10 hover:bg-primary/20 transition-colors">

            <Plus className="w-5 h-5 text-primary" />
          </button>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Cerca chat..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-muted/40 backdrop-blur-sm border-none rounded-xl h-9" />

        </div>
      </div>

      {/* Chat list */}
      <ScrollArea className="flex-1">
        <div className="px-2 pb-24">
          {loading ?
          <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
            </div> :
          filtered.length === 0 ?
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <MessageCircle className="w-12 h-12 mb-3 opacity-40" />
              <p className="text-sm">Nessuna conversazione</p>
              <button onClick={() => setShowNew(true)} className="text-primary text-sm mt-2 hover:underline">
                Inizia una nuova chat
              </button>
            </div> :

          filtered.map((chat, i) => {
            const unread = getUnreadCount(chat);
            return (
              <motion.button
                key={chat.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                onClick={() => onSelectChat(chat.id)}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-2xl hover:bg-muted/40 transition-all active:scale-[0.98]">

                  <Avatar className="w-12 h-12 flex-shrink-0">
                    <AvatarImage src={getChatAvatar(chat) || undefined} />
                    <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                      {chat.is_group ? <Users className="w-5 h-5" /> : getChatDisplayName(chat).charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="flex items-center justify-between">
                      <span className={`text-[15px] font-semibold truncate ${unread ? "text-foreground" : "text-foreground"}`}>
                        {getChatDisplayName(chat)}
                      </span>
                      <span className="text-[11px] text-muted-foreground flex-shrink-0 ml-2">
                        {getLastMessageTime(chat)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-0.5">
                      <p className={`text-[13px] truncate ${unread ? "text-foreground font-medium" : "text-muted-foreground"}`}>
                        {getLastMessagePreview(chat)}
                      </p>
                      {unread > 0 &&
                    <span className="ml-2 flex-shrink-0 w-5 h-5 rounded-full bg-primary text-primary-foreground text-[11px] flex items-center justify-center font-bold">
                          {unread}
                        </span>
                    }
                    </div>
                  </div>
                </motion.button>);

          })
          }
        </div>
      </ScrollArea>

      <NewChatDialog
        open={showNew}
        onOpenChange={setShowNew}
        contacts={contacts}
        onAddContact={onAddContact}
        onCreateChat={async (ids, isGroup, name) => {
          const chatId = await onCreateChat(ids, isGroup, name);
          if (chatId) {
            setShowNew(false);
            onSelectChat(chatId);
          }
        }} />

    </div>);

};